import API from '../api';

const VISIBILITY_THRESHOLD = 0.5;
const VISIBILITY_SAMPLING_SIZE = 25;
const OBSERVER_THRESHOLDS = [0, VISIBILITY_THRESHOLD, 1];
const EVENT_VIEW_BATCH_SIZE = 5;
const VIEW_LOG_FLUSH_DELAY_MS = 2000;

const visibilityStates = new Map();
const activeElements = new Set();
const pendingElements = new Set();

let visibilityObserver = null;
let globalListenersAttached = false;
let scheduledFrame = 0;
let shouldEvaluateActiveElements = false;

function getOccludedRatio(el, sample = VISIBILITY_SAMPLING_SIZE) {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
        return 1;
    }

    const grid = Math.max(1, Math.round(Math.sqrt(sample)));
    let visibleCount = 0;
    let total = 0;

    for (let r = 0; r < grid; r++) {
        for (let c = 0; c < grid; c++) {
            const x = rect.left + ((c + 0.5) / grid) * rect.width;
            const y = rect.top + ((r + 0.5) / grid) * rect.height;
            const topEl = document.elementFromPoint(x, y);
            if (!topEl) {
                continue;
            }
            total++;
            if (topEl === el || el.contains(topEl)) {
                visibleCount++;
            }
        }
    }

    return total ? 1 - visibleCount / total : 1;
}

function applyBindingValue(state, bindingValue) {
    const value = bindingValue || {};
    state.onShow = value.onShow || (() => {});
    state.onHide = value.onHide || (() => {});
    state.onEventShow = value.onEventShow || (() => {});
    state.sample = value.sample || VISIBILITY_SAMPLING_SIZE;
}

function emitVisibility(state, visible) {
    if (state.lastVisible === visible) {
        return;
    }

    state.lastVisible = visible;

    if (visible) {
        state.onShow();
        state.onEventShow();
        return;
    }

    state.onHide();
}

function evaluateElement(el) {
    const state = visibilityStates.get(el);
    if (!state) {
        return;
    }

    if (state.lastIntersection < VISIBILITY_THRESHOLD) {
        emitVisibility(state, false);
        return;
    }

    const occludedRatio = getOccludedRatio(el, state.sample);
    const visibleByOcclusion = 1 - occludedRatio;
    const effectiveVisibleRatio = state.lastIntersection * visibleByOcclusion;

    emitVisibility(state, effectiveVisibleRatio >= VISIBILITY_THRESHOLD);
}

function flushEvaluations() {
    scheduledFrame = 0;

    const targets = shouldEvaluateActiveElements
            ? Array.from(new Set([...activeElements, ...pendingElements]))
            : Array.from(pendingElements);

    shouldEvaluateActiveElements = false;
    pendingElements.clear();

    targets.forEach((el) => {
        evaluateElement(el);
    });
}

function scheduleEvaluation(forceActiveEvaluation = false) {
    if (forceActiveEvaluation) {
        shouldEvaluateActiveElements = true;
    }

    if (scheduledFrame) {
        return;
    }

    scheduledFrame = requestAnimationFrame(flushEvaluations);
}

function queueElementEvaluation(el) {
    if (!visibilityStates.has(el)) {
        return;
    }

    pendingElements.add(el);
    scheduleEvaluation(false);
}

function handleGlobalVisibilityChange() {
    if (activeElements.size === 0) {
        return;
    }

    scheduleEvaluation(true);
}

function handlePageHide() {
    sendEventShow();
}

function handleDocumentVisibilityChange() {
    if (document.hidden) {
        handlePageHide();
    }
}

function attachGlobalListeners() {
    if (globalListenersAttached) {
        return;
    }

    window.addEventListener('scroll', handleGlobalVisibilityChange, true);
    window.addEventListener('resize', handleGlobalVisibilityChange);
    window.addEventListener('focus', handleGlobalVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('overlay-change', handleGlobalVisibilityChange);
    document.addEventListener('visibilitychange', handleDocumentVisibilityChange);

    globalListenersAttached = true;
}

function detachGlobalListeners() {
    if (!globalListenersAttached) {
        return;
    }

    window.removeEventListener('scroll', handleGlobalVisibilityChange, true);
    window.removeEventListener('resize', handleGlobalVisibilityChange);
    window.removeEventListener('focus', handleGlobalVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('overlay-change', handleGlobalVisibilityChange);
    document.removeEventListener('visibilitychange', handleDocumentVisibilityChange);

    globalListenersAttached = false;
}

function ensureObserver() {
    if (visibilityObserver) {
        return visibilityObserver;
    }

    visibilityObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const state = visibilityStates.get(entry.target);
                    if (!state) {
                        return;
                    }

                    const intersectionRatio = entry.intersectionRatio || 0;
                    state.lastIntersection = intersectionRatio;

                    if (intersectionRatio > 0) {
                        activeElements.add(entry.target);
                    } else {
                        activeElements.delete(entry.target);
                    }

                    queueElementEvaluation(entry.target);
                });
            },
            {threshold: OBSERVER_THRESHOLDS}
    );

    return visibilityObserver;
}

function resetManagerIfIdle() {
    if (visibilityStates.size > 0) {
        return;
    }

    if (visibilityObserver) {
        visibilityObserver.disconnect();
        visibilityObserver = null;
    }

    detachGlobalListeners();
    activeElements.clear();
    pendingElements.clear();
    shouldEvaluateActiveElements = false;

    if (scheduledFrame) {
        cancelAnimationFrame(scheduledFrame);
        scheduledFrame = 0;
    }
}

function registerElement(el, bindingValue) {
    const state = {
        lastVisible: false,
        lastIntersection: 0,
        onShow: () => {},
        onHide: () => {},
        onEventShow: () => {},
        sample: VISIBILITY_SAMPLING_SIZE,
    };

    applyBindingValue(state, bindingValue);
    visibilityStates.set(el, state);

    attachGlobalListeners();
    ensureObserver().observe(el);
}

function unregisterElement(el) {
    if (visibilityObserver) {
        visibilityObserver.unobserve(el);
    }

    visibilityStates.delete(el);
    activeElements.delete(el);
    pendingElements.delete(el);

    resetManagerIfIdle();
}

export const Visibility50Directive = {
    mounted(el, binding) {
        registerElement(el, binding.value);
    },

    updated(el, binding) {
        const state = visibilityStates.get(el);
        if (!state) {
            return;
        }

        applyBindingValue(state, binding.value);
        queueElementEvaluation(el);
    },

    unmounted(el) {
        unregisterElement(el);
    },
};

let eventList = [];
let eventFlushTimer = null;

function getEventViewIdx(event) {
    if (!event) {
        return null;
    }

    if (event.evIdx) {
        return event.evIdx;
    }

    if (event.ev_idx) {
        return event.ev_idx;
    }

    if (event.eventIdx) {
        return event.eventIdx;
    }

    return null;
}

function clearEventFlushTimer() {
    if (!eventFlushTimer) {
        return;
    }

    clearTimeout(eventFlushTimer);
    eventFlushTimer = null;
}

function scheduleEventFlush() {
    clearEventFlushTimer();
    eventFlushTimer = setTimeout(() => {
        sendEventShow();
    }, VIEW_LOG_FLUSH_DELAY_MS);
}

export function setShowedEventIdx(event) {
    const eventIdx = getEventViewIdx(event);
    if (!eventIdx) {
        return;
    }

    if (eventList.length > 0 && eventList.includes(eventIdx)) {
        return;
    }

    eventList.push(eventIdx);

    if (eventList.length >= EVENT_VIEW_BATCH_SIZE) {
        sendEventShow();
        return;
    }

    scheduleEventFlush();
}

export async function sendEventShow() {
    if (eventList.length === 0) {
        clearEventFlushTimer();
        return;
    }

    clearEventFlushTimer();
    const sendLists = [...eventList];
    eventList = [];

    try {
        await API.$gift.eventViewCountUp({evIdxes: sendLists});
    } catch (e) {
        console.error(e);
    }
}

export default Visibility50Directive;
